import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  X, Camera, Image as ImageIcon, Trash2, MapPin, Loader2, Plus,
  ChevronDown, ChevronRight, CalendarClock, WifiOff, Check,
} from 'lucide-react';
import Button from '../ui/Button';
import cultivarService from '../../services/cultivar';
import evaluationQueue, { newUuid, type QueuedPhoto } from '../../services/evaluationQueue';
import { useLocation } from '../../hooks/useLocation';
import GrowingRegionPicker from './GrowingRegionPicker';
import { byCountryThenName } from './growingRegionDraft';
import {
  SEASON_STAGES, SEASON_STAGE_LABELS,
  type AttributeDefinition, type SeasonStage, type SeasonPosition, type EvaluationInput,
  type Hemisphere, type GrowingRegion,
} from '../../types/cultivar';

// Full-screen evaluation capture form.
//
// Designed thumb-first: this is used one-handed, outdoors, in sunlight, while
// holding fruit in the other hand. Hence full-screen rather than a centred
// dialog, a sticky footer so Save is always reachable without scrolling, and
// collapsed-by-default attribute sections so the initial view isn't a wall of
// forty inputs.

interface Props {
  open: boolean;
  cultivarId: number;
  cultivarName: string;
  onClose: () => void;
  /** Fired after a successful enqueue so the parent can refresh its list. */
  onSaved: () => void;
  /** Pre-expand these attributes — set when arriving from a follow-up reminder. */
  focusAttributeIds?: number[];
}

type ValueMap = Record<number, string>;

interface DraftPhoto {
  clientUuid: string;
  blob: Blob;
  fileName: string;
  caption: string;
  previewUrl: string;
  latitude: number | null;
  longitude: number | null;
  takenAt: string;
}

interface DraftFollowup {
  key: string;
  due_date: string;
  focus_attribute_ids: number[];
  note: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  fruit_quality: 'Fruit quality',
  tree_habit: 'Tree habit',
  season: 'Season & timing',
  disease: 'Disease & pests',
  issues: 'Issues',
  custom: 'Other',
};

const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

function todayStr(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function addDaysStr(base: string, days: number): string {
  // Parse as local midnight rather than UTC — `new Date('2026-07-19')` is UTC
  // and drifts a day backwards in Australian timezones.
  const [y, m, d] = base.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${dt.getFullYear()}-${mm}-${dd}`;
}

/** "62 days before harvest" reads better than "-62". */
function describeSeasonPosition(pos: SeasonPosition | null): string | null {
  if (!pos || pos.days_from_harvest == null) return null;
  const d = pos.days_from_harvest;
  if (d === 0) return 'At expected harvest date';
  if (d < 0) return `${Math.abs(d)} days before expected harvest`;
  return `${d} days after expected harvest`;
}

export function EvaluationForm({ open, cultivarId, cultivarName, onClose, onSaved, focusAttributeIds }: Props) {
  const [defs, setDefs] = useState<AttributeDefinition[]>([]);
  const [loadingDefs, setLoadingDefs] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [evaluatedOn, setEvaluatedOn] = useState(todayStr());
  const [seasonPosition, setSeasonPosition] = useState<SeasonPosition | null>(null);
  const [seasonStage, setSeasonStage] = useState<SeasonStage | ''>('');
  const [stageTouched, setStageTouched] = useState(false);

  const [values, setValues] = useState<ValueMap>({});
  const [rangeMax, setRangeMax] = useState<ValueMap>({});
  const [photos, setPhotos] = useState<DraftPhoto[]>([]);
  const [followups, setFollowups] = useState<DraftFollowup[]>([]);

  const [locationLabel, setLocationLabel] = useState('');
  const [regions, setRegions] = useState<GrowingRegion[]>([]);
  const [growingRegionId, setGrowingRegionId] = useState(0);
  const [coords, setCoords] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);
  // Only used when GPS is unavailable. Without a hemisphere the evaluation
  // can't be compared against ones recorded on the other side of the equator.
  const [manualHemisphere, setManualHemisphere] = useState<Hemisphere | ''>('');
  const [overallScore, setOverallScore] = useState<number | null>(null);
  const [summary, setSummary] = useState('');
  const [conditionsNotes, setConditionsNotes] = useState('');

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const { requestLocation, loading: locating } = useLocation();

  // ---------------------------------------------------------------------
  // Load attribute definitions
  // ---------------------------------------------------------------------

  // This component stays mounted while closed (`open` only short-circuits the
  // render), so state must be cleared explicitly on each open — otherwise the
  // next evaluation opens pre-filled with the previous one's readings.
  // Declared before the season-position and focus-expand effects so those run
  // against a clean slate rather than being undone by the reset.
  useEffect(() => {
    if (!open) return;
    setEvaluatedOn(todayStr());
    setSeasonPosition(null);
    setSeasonStage('');
    setStageTouched(false);
    setValues({});
    setRangeMax({});
    setPhotos((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      return [];
    });
    setFollowups([]);
    setLocationLabel('');
    setGrowingRegionId(0);
    setCoords(null);
    setManualHemisphere('');
    setOverallScore(null);
    setSummary('');
    setConditionsNotes('');
    setExpanded({});
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingDefs(true);
    cultivarService.listAttributeDefinitions()
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) setDefs(res.data.filter((d) => d.active));
      })
      .catch(() => { if (!cancelled) setError('Could not load evaluation fields.'); })
      .finally(() => { if (!cancelled) setLoadingDefs(false); });
    // Regions are loaded alongside, not gated on: the picker is optional and a
    // failure here must not stop a visit being recorded.
    cultivarService.listGrowingRegions()
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) setRegions(res.data);
      })
      .catch(() => { /* offline or unavailable — the field simply stays empty */ });
    return () => { cancelled = true; };
  }, [open]);

  // Track connectivity so the Save button can tell the user what will happen.
  useEffect(() => {
    const on = () => setIsOffline(false);
    const off = () => setIsOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // ---------------------------------------------------------------------
  // Season position — refreshed whenever the evaluation date changes
  // ---------------------------------------------------------------------

  // Re-runs when GPS is captured: the harvest reference is hemisphere-specific,
  // so a position calculated before we knew where we were can be half a year out.
  useEffect(() => {
    if (!open || !evaluatedOn) return;
    let cancelled = false;
    cultivarService.getSeasonPosition(cultivarId, evaluatedOn, { latitude: coords?.lat ?? null })
      .then((res) => {
        if (cancelled || !res.success || !res.data) return;
        setSeasonPosition(res.data);
        // Only auto-fill the stage while the user hasn't overridden it — the
        // person in the orchard outranks our inference.
        if (!stageTouched && res.data.season_stage) setSeasonStage(res.data.season_stage);
      })
      // Offline is expected here; the field is optional and the server
      // recomputes on submit anyway.
      .catch(() => { if (!cancelled) setSeasonPosition(null); });
    return () => { cancelled = true; };
  }, [open, cultivarId, evaluatedOn, stageTouched, coords?.lat]);

  // Expand the sections a follow-up reminder pointed us at, so the user lands
  // directly on what they came back to re-check.
  useEffect(() => {
    if (!open || !focusAttributeIds?.length || defs.length === 0) return;
    const cats = new Set(
      defs.filter((d) => focusAttributeIds.includes(d.id)).map((d) => d.category)
    );
    if (cats.size > 0) {
      setExpanded((prev) => {
        const next = { ...prev };
        cats.forEach((c) => { next[c] = true; });
        return next;
      });
    }
  }, [open, focusAttributeIds, defs]);

  // Mirror photos into a ref so the unmount cleanup sees the *current* list.
  // A cleanup closing over `photos` with an empty dep array would capture the
  // initial empty array and revoke nothing.
  const photosRef = useRef<DraftPhoto[]>([]);
  photosRef.current = photos;

  useEffect(() => {
    return () => { photosRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl)); };
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, AttributeDefinition[]>();
    for (const d of defs) {
      if (!map.has(d.category)) map.set(d.category, []);
      map.get(d.category)!.push(d);
    }
    return Array.from(map.entries());
  }, [defs]);

  const filledCount = useMemo(
    () => Object.values(values).filter((v) => v !== '' && v != null).length,
    [values]
  );

  // A maturity date with no region attached is the one reading this form can
  // silently waste: deriveObservedRecords attributes an observation through the
  // evaluation's region (or its farm's), so an unattributed one never reaches
  // the maturity record and the harvest charts carry on as if it were never
  // taken. Worth a nudge, not a block — the reading itself is still useful.
  const maturityWithoutRegion = useMemo(() => {
    if (growingRegionId) return false;
    const def = defs.find((d) => d.slug === 'maturity_date');
    if (!def) return false;
    const raw = values[def.id];
    return Boolean(raw && raw !== '');
  }, [defs, values, growingRegionId]);

  // ---------------------------------------------------------------------
  // Photos
  // ---------------------------------------------------------------------

  const addPhoto = useCallback((file: File) => {
    if (file.size > MAX_PHOTO_BYTES) {
      setError('Photos must be under 10MB.');
      return;
    }
    setError(null);
    setPhotos((prev) => [...prev, {
      clientUuid: newUuid(),
      blob: file,
      fileName: file.name || 'photo.jpg',
      caption: '',
      previewUrl: URL.createObjectURL(file),
      // Stamp each photo with wherever we were when it was taken — walking a
      // block means the last photo can be a long way from the first.
      latitude: coords?.lat ?? null,
      longitude: coords?.lng ?? null,
      takenAt: new Date().toISOString(),
    }]);
  }, [coords]);

  const removePhoto = (clientUuid: string) => {
    setPhotos((prev) => {
      const target = prev.find((p) => p.clientUuid === clientUuid);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.clientUuid !== clientUuid);
    });
  };

  const captureLocation = async () => {
    try {
      const res = await requestLocation();
      if (res) {
        setCoords({ lat: res.coords.latitude, lng: res.coords.longitude, accuracy: res.coords.accuracy });
        setError(null);
      }
    } catch {
      setError('Could not get GPS position. You can still save without it.');
    }
  };

  // ---------------------------------------------------------------------
  // Follow-ups
  // ---------------------------------------------------------------------

  const addFollowup = (days: number) => {
    setFollowups((prev) => [...prev, {
      key: newUuid(),
      due_date: addDaysStr(evaluatedOn, days),
      focus_attribute_ids: [],
      note: '',
    }]);
  };

  const updateFollowup = (key: string, patch: Partial<DraftFollowup>) => {
    setFollowups((prev) => prev.map((f) => (f.key === key ? { ...f, ...patch } : f)));
  };

  // ---------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------

  /** Convert a raw input string into the JSON shape the attribute expects. */
  const coerceValue = (def: AttributeDefinition, raw: string): unknown | undefined => {
    if (raw === '' || raw == null) return undefined;
    switch (def.data_type) {
      case 'number': {
        const n = Number(raw);
        return Number.isNaN(n) ? undefined : n;
      }
      case 'boolean':
        return raw === 'true';
      case 'range': {
        const min = Number(raw);
        const max = Number(rangeMax[def.id]);
        if (Number.isNaN(min) || Number.isNaN(max)) return undefined;
        return { min, max };
      }
      default:
        return raw;
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const payloadValues: EvaluationInput['values'] = [];
      for (const def of defs) {
        const coerced = coerceValue(def, values[def.id] ?? '');
        if (coerced !== undefined) payloadValues.push({ attribute_id: def.id, value: coerced });
      }

      if (payloadValues.length === 0 && photos.length === 0 && !summary.trim()) {
        setError('Record at least one reading, photo, or summary note before saving.');
        setSaving(false);
        return;
      }

      const payload: EvaluationInput = {
        client_uuid: newUuid(),
        evaluated_on: evaluatedOn,
        // Season year follows the evaluation date; the server tags seasonal
        // attributes with it so they slot into existing season filters.
        season_year: Number(evaluatedOn.slice(0, 4)),
        season_stage: seasonStage || null,
        latitude: coords?.lat ?? null,
        longitude: coords?.lng ?? null,
        location_label: locationLabel.trim() || null,
        growing_region_id: growingRegionId || null,
        // The server prefers latitude; this covers a visit recorded without
        // location permission, where the user picked the hemisphere by hand.
        hemisphere: coords ? (coords.lat < 0 ? 'S' : 'N') : manualHemisphere || null,
        overall_score: overallScore,
        summary: summary.trim() || null,
        conditions_notes: conditionsNotes.trim() || null,
        status: 'submitted',
        values: payloadValues,
        followups: followups.map((f) => ({
          due_date: f.due_date,
          focus_attribute_ids: f.focus_attribute_ids,
          note: f.note.trim() || null,
        })),
      };

      const queuedPhotos: QueuedPhoto[] = photos.map((p) => ({
        clientUuid: p.clientUuid,
        blob: p.blob,
        fileName: p.fileName,
        caption: p.caption.trim() || null,
        latitude: p.latitude,
        longitude: p.longitude,
        takenAt: p.takenAt,
      }));

      // Always go through the queue, online or not. The record is durable
      // before we ever touch the network, so a dropout mid-submit can't lose
      // the visit — and the UI can close immediately either way.
      await evaluationQueue.enqueue(cultivarId, cultivarName, payload, queuedPhotos);

      photos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      onSaved();
      onClose();
    } catch (e) {
      const err = e as { message?: string };
      setError(err.message || 'Could not save the evaluation.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const positionText = describeSeasonPosition(seasonPosition);

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Sticky header — safe-area padding so the close button isn't under
          the notch on iOS. */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-white shrink-0"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-neutral-900 truncate">New evaluation</h2>
          <p className="text-xs text-neutral-500 truncate">{cultivarName}</p>
        </div>
        <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700 p-2 -mr-2" aria-label="Close">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {error && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-3 text-sm text-error-700">
            {error}
          </div>
        )}

        {isOffline && (
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 text-sm text-warning-800 flex items-start gap-2">
            <WifiOff className="h-4 w-4 mt-0.5 shrink-0" />
            <span>You're offline. This evaluation will be saved on your device and uploaded automatically when signal returns.</span>
          </div>
        )}

        {/* ---------------- When ---------------- */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-neutral-900">When</h3>
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Date of visit</label>
            <input
              type="date"
              value={evaluatedOn}
              onChange={(e) => setEvaluatedOn(e.target.value)}
              className="input-professional w-full"
            />
          </div>

          {positionText && (
            <div className="rounded-lg bg-primary-50 border border-primary-200 px-3 py-2 text-sm text-primary-800">
              <span className="font-medium">{positionText}</span>
              {seasonPosition?.reference_maturity_date && (
                <span className="block text-xs text-primary-700 mt-0.5">
                  Based on recorded maturity date {seasonPosition.reference_maturity_date}
                  {seasonPosition.reference_hemisphere &&
                    ` (${seasonPosition.reference_hemisphere === 'S' ? 'southern' : 'northern'} hemisphere)`}
                </span>
              )}
            </div>
          )}

          {seasonPosition?.reference_is_cross_hemisphere && (
            <div className="rounded-lg bg-warning-50 border border-warning-200 px-3 py-2 text-xs text-warning-800">
              No harvest date has been recorded for this cultivar in this hemisphere yet,
              so the figure above uses the other hemisphere's harvest and may be out by
              around six months. Record a “Maturity date” reading here to fix it.
            </div>
          )}
          {seasonPosition && seasonPosition.days_from_harvest == null && (
            <p className="text-xs text-neutral-500">
              No maturity date recorded for this cultivar yet, so season position can't be calculated.
              Record a “Maturity date” reading below to enable it.
            </p>
          )}

          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Growth stage</label>
            <select
              value={seasonStage}
              onChange={(e) => { setSeasonStage(e.target.value as SeasonStage | ''); setStageTouched(true); }}
              className="input-professional w-full"
            >
              <option value="">Not specified</option>
              {SEASON_STAGES.map((s) => (
                <option key={s} value={s}>{SEASON_STAGE_LABELS[s]}</option>
              ))}
            </select>
          </div>
        </section>

        {/* ---------------- Where ---------------- */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-neutral-900">Where</h3>
          <div className="flex gap-2">
            <Button variant="outline" onClick={captureLocation} disabled={locating} className="shrink-0">
              {locating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <MapPin className="h-4 w-4 mr-1" />}
              {coords ? 'Update GPS' : 'Capture GPS'}
            </Button>
            {coords && (
              <div className="flex items-center text-xs text-success-700 min-w-0">
                <Check className="h-4 w-4 mr-1 shrink-0" />
                <span className="truncate">
                  {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)} (±{Math.round(coords.accuracy)}m)
                </span>
              </div>
            )}
          </div>
          {/* GPS gives us the hemisphere for free. Without it, ask — otherwise
              this evaluation can't be lined up against ones from the other
              side of the equator. */}
          {!coords && (
            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1">Hemisphere</label>
              <select
                value={manualHemisphere}
                onChange={(e) => setManualHemisphere(e.target.value as Hemisphere | '')}
                className="input-professional w-full"
              >
                <option value="">Unknown</option>
                <option value="S">Southern</option>
                <option value="N">Northern</option>
              </select>
              <p className="text-[11px] text-neutral-500 mt-1">
                Set automatically when you capture GPS. Needed to compare this visit
                with evaluations recorded in the other hemisphere.
              </p>
            </div>
          )}

          <GrowingRegionPicker
            regions={regions}
            value={growingRegionId}
            onChange={setGrowingRegionId}
            onCreated={(r) => setRegions((prev) => [...prev, r].sort(byCountryThenName))}
            label="Growing region"
            canCreate={!isOffline}
          />
          <p className="text-[11px] text-neutral-500 -mt-1">
            The climate area this block sits in — what maturity dates are averaged by.
            Not a state or a licensing territory.
          </p>

          {maturityWithoutRegion && (
            <div className="rounded-lg bg-warning-50 border border-warning-200 px-3 py-2 text-xs text-warning-800">
              You've recorded a maturity date but no growing region, so it won't be
              counted towards this cultivar's harvest timing. Pick a region above.
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Location note</label>
            <input
              type="text"
              value={locationLabel}
              onChange={(e) => setLocationLabel(e.target.value)}
              placeholder="e.g. Smith orchard, row 12, north end"
              className="input-professional w-full"
            />
          </div>
        </section>

        {/* ---------------- Readings ---------------- */}
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-neutral-900">Readings</h3>
            <span className="text-xs text-neutral-500">{filledCount} recorded</span>
          </div>

          {loadingDefs ? (
            <div className="flex items-center gap-2 text-sm text-neutral-500 py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading fields…
            </div>
          ) : (
            grouped.map(([category, catDefs]) => {
              const isOpen = expanded[category] ?? false;
              const catFilled = catDefs.filter((d) => (values[d.id] ?? '') !== '').length;
              return (
                <div key={category} className="border border-neutral-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpanded((p) => ({ ...p, [category]: !isOpen }))}
                    className="w-full flex items-center justify-between px-3 py-3 bg-neutral-50 text-left"
                  >
                    <span className="text-sm font-medium text-neutral-800">
                      {CATEGORY_LABELS[category] || category}
                    </span>
                    <span className="flex items-center gap-2">
                      {catFilled > 0 && (
                        <span className="text-xs bg-primary-100 text-primary-700 rounded-full px-2 py-0.5">
                          {catFilled}
                        </span>
                      )}
                      {isOpen ? <ChevronDown className="h-4 w-4 text-neutral-500" /> : <ChevronRight className="h-4 w-4 text-neutral-500" />}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="p-3 space-y-3 divide-y divide-neutral-100">
                      {catDefs.map((def) => (
                        <div key={def.id} className="pt-3 first:pt-0">
                          <label className="block text-xs font-medium text-neutral-700 mb-1">
                            {def.display_name}
                            {def.unit && <span className="text-neutral-400 font-normal"> ({def.unit})</span>}
                            {focusAttributeIds?.includes(def.id) && (
                              <span className="ml-2 text-[10px] uppercase tracking-wide bg-warning-100 text-warning-800 rounded px-1.5 py-0.5">
                                Re-check
                              </span>
                            )}
                          </label>

                          {def.data_type === 'enum' ? (
                            <select
                              value={values[def.id] ?? ''}
                              onChange={(e) => setValues((p) => ({ ...p, [def.id]: e.target.value }))}
                              className="input-professional w-full"
                            >
                              <option value="">—</option>
                              {(def.enum_options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : def.data_type === 'boolean' ? (
                            <select
                              value={values[def.id] ?? ''}
                              onChange={(e) => setValues((p) => ({ ...p, [def.id]: e.target.value }))}
                              className="input-professional w-full"
                            >
                              <option value="">—</option>
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                          ) : def.data_type === 'range' ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                inputMode="decimal"
                                placeholder="Min"
                                value={values[def.id] ?? ''}
                                onChange={(e) => setValues((p) => ({ ...p, [def.id]: e.target.value }))}
                                className="input-professional w-full"
                              />
                              <span className="text-neutral-400">–</span>
                              <input
                                type="number"
                                inputMode="decimal"
                                placeholder="Max"
                                value={rangeMax[def.id] ?? ''}
                                onChange={(e) => setRangeMax((p) => ({ ...p, [def.id]: e.target.value }))}
                                className="input-professional w-full"
                              />
                            </div>
                          ) : (
                            <input
                              type={def.data_type === 'date' ? 'date' : def.data_type === 'number' ? 'number' : 'text'}
                              // Surfaces the numeric keypad on mobile without
                              // blocking decimals the way type=number alone can.
                              inputMode={def.data_type === 'number' ? 'decimal' : undefined}
                              value={values[def.id] ?? ''}
                              onChange={(e) => setValues((p) => ({ ...p, [def.id]: e.target.value }))}
                              className="input-professional w-full"
                            />
                          )}

                          {def.description && (
                            <p className="text-[11px] text-neutral-500 mt-1">{def.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </section>

        {/* ---------------- Photos ---------------- */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-neutral-900">Photos</h3>
          <div className="flex gap-2">
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) addPhoto(f);
                e.target.value = '';
              }}
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                Array.from(e.target.files || []).forEach(addPhoto);
                e.target.value = '';
              }}
            />
            <Button variant="primary" onClick={() => cameraRef.current?.click()} className="flex-1">
              <Camera className="h-4 w-4 mr-1" /> Take photo
            </Button>
            <Button variant="outline" onClick={() => fileRef.current?.click()} className="flex-1">
              <ImageIcon className="h-4 w-4 mr-1" /> Choose
            </Button>
          </div>

          {photos.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {photos.map((p) => (
                <div key={p.clientUuid} className="border border-neutral-200 rounded-lg overflow-hidden">
                  <div className="relative">
                    <img src={p.previewUrl} alt="" className="w-full h-28 object-cover" />
                    <button
                      onClick={() => removePhoto(p.clientUuid)}
                      className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1.5"
                      aria-label="Remove photo"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={p.caption}
                    onChange={(e) => setPhotos((prev) => prev.map((x) =>
                      x.clientUuid === p.clientUuid ? { ...x, caption: e.target.value } : x
                    ))}
                    placeholder="Caption"
                    className="w-full px-2 py-1.5 text-xs border-t border-neutral-200 focus:outline-none"
                  />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ---------------- Overall ---------------- */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-neutral-900">Overall impression</h3>
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-2">
              Score {overallScore != null && <span className="text-primary-600 font-semibold">{overallScore}/10</span>}
            </label>
            {/* Discrete buttons rather than a slider — far easier to hit
                accurately with a thumb, and the chosen value is unambiguous. */}
            <div className="grid grid-cols-10 gap-1">
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setOverallScore(overallScore === n ? null : n)}
                  className={`h-9 rounded text-xs font-medium border transition-colors ${
                    overallScore === n
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-neutral-600 border-neutral-300'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Summary</label>
            <textarea
              rows={3}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="What stood out about this cultivar today?"
              className="input-professional w-full"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Conditions</label>
            <textarea
              rows={2}
              value={conditionsNotes}
              onChange={(e) => setConditionsNotes(e.target.value)}
              placeholder="Weather, irrigation, recent sprays, anything affecting what you saw"
              className="input-professional w-full"
            />
          </div>
        </section>

        {/* ---------------- Follow-ups ---------------- */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-neutral-900">Remind me to re-check</h3>
          <p className="text-xs text-neutral-500">
            Schedule a return visit. You'll get a notification on the day it's due.
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'In 1 week', days: 7 },
              { label: 'In 2 weeks', days: 14 },
              { label: 'In 1 month', days: 30 },
            ].map((preset) => (
              <Button key={preset.days} variant="outline" size="sm" onClick={() => addFollowup(preset.days)}>
                <Plus className="h-3.5 w-3.5 mr-1" /> {preset.label}
              </Button>
            ))}
          </div>

          {followups.map((f) => (
            <div key={f.key} className="border border-neutral-200 rounded-lg p-3 space-y-2 bg-neutral-50">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <CalendarClock className="h-4 w-4 text-neutral-500 shrink-0" />
                  <input
                    type="date"
                    value={f.due_date}
                    onChange={(e) => updateFollowup(f.key, { due_date: e.target.value })}
                    className="input-professional w-full"
                  />
                </div>
                <button
                  onClick={() => setFollowups((prev) => prev.filter((x) => x.key !== f.key))}
                  className="text-neutral-400 hover:text-error-600 p-1"
                  aria-label="Remove reminder"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <input
                type="text"
                value={f.note}
                onChange={(e) => updateFollowup(f.key, { note: e.target.value })}
                placeholder="What needs re-checking?"
                className="input-professional w-full"
              />
              <details>
                <summary className="text-xs text-primary-600 cursor-pointer">
                  Focus on specific readings ({f.focus_attribute_ids.length} selected)
                </summary>
                <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                  {defs.map((d) => (
                    <label key={d.id} className="flex items-center gap-2 text-xs text-neutral-700">
                      <input
                        type="checkbox"
                        checked={f.focus_attribute_ids.includes(d.id)}
                        onChange={(e) => updateFollowup(f.key, {
                          focus_attribute_ids: e.target.checked
                            ? [...f.focus_attribute_ids, d.id]
                            : f.focus_attribute_ids.filter((x) => x !== d.id),
                        })}
                      />
                      {d.display_name}
                    </label>
                  ))}
                </div>
              </details>
            </div>
          ))}
        </section>
      </div>

      {/* Sticky footer — Save must be reachable without scrolling to the
          bottom of a long form. */}
      <div
        className="border-t border-neutral-200 bg-white px-4 py-3 flex gap-3 shrink-0"
        style={{ paddingBottom: 'max(0.75rem, var(--safe-bottom))' }}
      >
        <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} isLoading={saving} className="flex-[2]">
          {isOffline ? 'Save offline' : 'Save evaluation'}
        </Button>
      </div>
    </div>
  );
}

export default EvaluationForm;
